import { useState, useRef, useCallback, useEffect, useMemo, ChangeEvent } from 'react';
import { IMessage } from '../../../dataProvider/DataProvider';
import { _dataProvider } from '../../../dataProvider/DataProvider';
import { getJwtObj } from '../../../SharedCommon/utils';
import lodash from 'lodash';

export type FilterType = 'none' | 'file' | 'user' | 'group';

export interface IFilterState {
  activeFilter: FilterType;
  filteredMessages: IMessage[];
}

interface IUseMessageFiltersOptions {
  groupId: number;
  messages: IMessage[];
  mapMessage: (data: any) => IMessage[];
  onFilterStateChange?: (filterType: FilterType) => void;
  groupInfo?: any;
  shareInGroup?: number;
}

interface IUseMessageFiltersReturn {
  searchState: { inSearch: boolean; searchedMessages: IMessage[] };
  setSearchState: React.Dispatch<React.SetStateAction<{ inSearch: boolean; searchedMessages: IMessage[] }>>;
  filterState: IFilterState;
  setFilterState: React.Dispatch<React.SetStateAction<IFilterState>>;
  showSearchInput: boolean;
  setShowSearchInput: React.Dispatch<React.SetStateAction<boolean>>;
  chineseSearchResults: string[];
  setChineseSearchResults: React.Dispatch<React.SetStateAction<string[]>>;
  showChineseResults: boolean;
  setShowChineseResults: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: React.MutableRefObject<string>;
  applyFilter: (filterType: FilterType) => Promise<void>;
  getCurrentMessages: () => IMessage[];
  handleSearchChange: (e?: ChangeEvent<HTMLInputElement>, value?: string) => void;
  clearSearchState: () => void;
}

type MessageFilterFn = (msgs: IMessage[]) => Promise<IMessage[]> | IMessage[];

export const useMessageFilters = ({
  groupId,
  messages,
  mapMessage,
  onFilterStateChange,
  groupInfo,
  shareInGroup
}: IUseMessageFiltersOptions): IUseMessageFiltersReturn => {
  const [searchState, setSearchState] = useState<{ inSearch: boolean; searchedMessages: IMessage[] }>({
    inSearch: false,
    searchedMessages: []
  });
  const [filterState, setFilterState] = useState<IFilterState>({
    activeFilter: 'none',
    filteredMessages: []
  });
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [chineseSearchResults, setChineseSearchResults] = useState<string[]>([]);
  const [showChineseResults, setShowChineseResults] = useState(false);
  const searchText = useRef('');
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const trimFileName = (name: string) => {
    if (name.startsWith('文件 ') || name.startsWith('音频 ') || name.startsWith('图片 ')) {
      return name.slice(3);
    }
    return name;
  };

  const messageFilters: Record<FilterType, MessageFilterFn> = useMemo(
    () => ({
      file: async () => {
        const fileList = await _dataProvider.getFileListInGroup(groupId);
        if (!fileList || !Array.isArray(fileList)) {
          return [];
        }
        return fileList
          .map((file) => {
            if (!file || typeof file !== 'object' || !file.fileId || !file.user) {
              return null;
            }
            const includeLoginId =
              !groupInfo?.disableMemberShowList ||
              (!!file.user?.id &&
                Array.isArray(groupInfo?.users) &&
                groupInfo.users.some((u: any) => {
                  const uId = u.userId ?? u.id;
                  return (
                    uId != null &&
                    String(uId) === String(file.user.id) &&
                    (u.isGroupLeader === true || u.isGroupLeader === 1)
                  );
                }));
            return {
              id: file.fileId,
              createdAt: new Date(file.time * 1000),
              text: trimFileName(file.displayName),
              time: file.time,
              user: {
                id: file.user.id,
                name: _dataProvider.getDisplayNameForUser(file.user, includeLoginId),
                avatar: _dataProvider.getAvatarSync(file.user.id),
                defaultAvatar: undefined as any
              },
              attachment: {
                url: _dataProvider.getFileUrlInChat(groupId, file.fileId),
                filename: file.displayName || 'file'
              },
              textStyle: {
                textDecoration: 'underline',
                textDecorationColor: file.user?.id === getJwtObj().userId ? 'white' : undefined
              }
            };
          })
          .filter((item) => item !== null) as IMessage[];
      },
      user: async () => {
        const taggedMessages = await _dataProvider.getTaggedMessages(groupId, 'user');
        if (!taggedMessages || !Array.isArray(taggedMessages)) {
          return [];
        }
        return mapMessage(taggedMessages);
      },
      group: async () => {
        const taggedMessages = await _dataProvider.getTaggedMessages(groupId, 'group');
        if (!taggedMessages || !Array.isArray(taggedMessages)) {
          return [];
        }
        return mapMessage(taggedMessages);
      },
      none: (msgs: IMessage[]) => msgs
    }),
    [groupId, mapMessage, groupInfo]
  );

  const applyFilter = useCallback(
    async (filterType: FilterType) => {
      setShowSearchInput(false);
      searchText.current = '';
      setSearchState({
        inSearch: false,
        searchedMessages: []
      });
      if (filterType === filterState.activeFilter) {
        const newFilter = 'none';
        setFilterState({
          activeFilter: newFilter,
          filteredMessages: messages
        });
        onFilterStateChange?.(newFilter);
      } else {
        const filterFn = messageFilters[filterType];
        const filtered = await filterFn(messages);
        const filteredMessages = Array.isArray(filtered) ? filtered : mapMessage(filtered);
        setFilterState({
          activeFilter: filterType,
          filteredMessages
        });
        onFilterStateChange?.(filterType);
      }
    },
    [messages, filterState.activeFilter, mapMessage, onFilterStateChange, messageFilters]
  );

  const getCurrentMessages = useCallback(() => {
    if (searchState.inSearch) {
      return searchState.searchedMessages;
    }
    if (filterState.activeFilter !== 'none') {
      return filterState.filteredMessages;
    }
    return messages;
  }, [
    searchState.inSearch,
    searchState.searchedMessages,
    filterState.activeFilter,
    filterState.filteredMessages,
    messages
  ]);

  const debouncedSearch = useCallback(
    (value: string) => {
      searchAbortControllerRef.current?.abort();
      searchAbortControllerRef.current = new AbortController();
      const { signal } = searchAbortControllerRef.current;

      searchText.current = value;
      if (!value || value.trim() === '') {
        setSearchState({ inSearch: false, searchedMessages: [] });
        return;
      }

      const query = new URLSearchParams({
        search: value
      });

      if (shareInGroup !== undefined) {
        query.set('shareInGroups', JSON.stringify([shareInGroup]));
      }

      _dataProvider
        .getGroupMessage(groupId, query.toString())
        .then((groupMessages) => {
          if (signal.aborted) {
            return;
          }
          if (!groupMessages || typeof groupMessages !== 'object') {
            setSearchState({ inSearch: true, searchedMessages: [] });
            return;
          }

          const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;
          if (!Array.isArray(messagesArray)) {
            setSearchState({ inSearch: true, searchedMessages: [] });
            return;
          }

          try {
            const searchedMessages = mapMessage(messagesArray);
            setSearchState({ inSearch: true, searchedMessages });
          } catch {
            setSearchState({ inSearch: true, searchedMessages: [] });
          }
        })
        .catch(() => {
          if (signal.aborted) {
            return;
          }
          setSearchState({ inSearch: true, searchedMessages: [] });
        });
    },
    [groupId, mapMessage, shareInGroup]
  );

  const debouncedSearchCallbackRef = useRef(debouncedSearch);
  debouncedSearchCallbackRef.current = debouncedSearch;

  const debouncedSearchFn = useMemo(
    () => lodash.debounce((value: string) => debouncedSearchCallbackRef.current(value), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearchFn.cancel();
      searchAbortControllerRef.current?.abort();
    };
  }, [debouncedSearchFn]);

  const clearSearchState = useCallback(() => {
    searchText.current = '';
    setSearchState({ inSearch: false, searchedMessages: [] });
  }, []);

  const handleSearchChange = useCallback(
    (e?: ChangeEvent<HTMLInputElement>, value?: string) => {
      if (!value) {
        clearSearchState();
        return;
      }
      debouncedSearchFn(value);
    },
    [debouncedSearchFn, clearSearchState]
  );

  return {
    searchState,
    setSearchState,
    filterState,
    setFilterState,
    showSearchInput,
    setShowSearchInput,
    chineseSearchResults,
    setChineseSearchResults,
    showChineseResults,
    setShowChineseResults,
    searchText,
    applyFilter,
    getCurrentMessages,
    handleSearchChange,
    clearSearchState
  };
};
